import React, { useEffect, useMemo, useRef, useState } from 'react';

// A small check-in / check-out calendar for the booking inquiry form.
// Two months side by side on wider screens, one on phones. Past dates are
// disabled. Arrow keys move between days.

export type Selecting = 'start' | 'end';

interface DateRangeCalendarProps {
  start: Date | null;
  end: Date | null;
  selecting: Selecting;
  onChange: (start: Date | null, end: Date | null, done: boolean) => void;
  id?: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && a.getTime() === b.getTime();
const key = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const nightsBetween = (a: Date, b: Date) => Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86400000);

const monthLabel = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const longLabel = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

// Two months side by side only when the calendar itself has room (~520px),
// e.g. the wide pop-up on desktop; one month in narrower spots such as the
// /book page card or a phone. Measured on the calendar, not the window.
const TWO_UP_MIN_WIDTH = 520;
const useTwoUp = (ref: React.RefObject<HTMLElement | null>) => {
  const [twoUp, setTwoUp] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setTwoUp(el.clientWidth >= TWO_UP_MIN_WIDTH);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return twoUp;
};

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({ start, end, selecting, onChange, id }) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [month, setMonth] = useState(() => addMonths(start ?? today, 0));
  const [hover, setHover] = useState<Date | null>(null);
  const [focusDay, setFocusDay] = useState<Date>(() => (selecting === 'end' && end ? end : start ?? today));
  const gridRef = useRef<HTMLDivElement>(null);
  const shouldFocus = useRef(false);
  const twoUp = useTwoUp(gridRef);
  const shown = twoUp ? 2 : 1;

  // Keep keyboard focus on the roving day after arrow-key moves.
  useEffect(() => {
    if (!shouldFocus.current) return;
    shouldFocus.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${key(focusDay)}"]`)?.focus();
  }, [focusDay, month]);

  const minMonth = addMonths(today, 0);
  const canGoBack = month.getTime() > minMonth.getTime();

  const choose = (day: Date) => {
    setFocusDay(day);
    if (selecting === 'start' || !start || day.getTime() <= start.getTime()) {
      // A new check-in. Keep the check-out only if it still comes after it.
      const keepEnd = selecting === 'start' && end && end.getTime() > day.getTime() ? end : null;
      onChange(day, keepEnd, false);
      return;
    }
    onChange(start, day, true);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    let next = addDays(focusDay, moves[event.key]);
    if (next.getTime() < today.getTime()) next = today;
    shouldFocus.current = true;
    setFocusDay(next);
    // Scroll the visible months when focus leaves them.
    const firstShown = month;
    const lastShown = addMonths(month, shown);
    if (next.getTime() < firstShown.getTime()) setMonth(addMonths(next, 0));
    else if (next.getTime() >= lastShown.getTime()) setMonth(addMonths(next, 1 - shown));
  };

  // While choosing a check-out, preview the stay up to the hovered day.
  const rangeEnd = end ?? (selecting === 'end' && start && hover && hover.getTime() > start.getTime() ? hover : null);

  const renderMonth = (first: Date, index: number) => {
    const lead = first.getDay();
    const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: days }, (_, i) => new Date(first.getFullYear(), first.getMonth(), i + 1)),
    ];
    const labelId = `${id ?? 'cal'}-m${index}`;
    return (
      <div className="cal-month" key={key(first)}>
        <p className="cal-month-label" id={labelId} aria-live={index === 0 ? 'polite' : undefined}>
          {monthLabel(first)}
        </p>
        <div className="cal-grid" role="group" aria-labelledby={labelId}>
          {WEEKDAYS.map((w) => (
            <span className="cal-weekday" aria-hidden="true" key={w}>{w}</span>
          ))}
          {cells.map((day, i) => {
            if (!day) return <span className="cal-blank" aria-hidden="true" key={`b${i}`} />;
            const past = day.getTime() < today.getTime();
            const isStart = sameDay(day, start);
            const isEnd = sameDay(day, rangeEnd);
            const inRange = !!start && !!rangeEnd && day.getTime() > start.getTime() && day.getTime() < rangeEnd.getTime();
            const classes = [
              'cal-day',
              isStart && 'is-start',
              isEnd && 'is-end',
              inRange && 'is-in-range',
              start && rangeEnd && (isStart || isEnd) && 'has-range',
              sameDay(day, today) && 'is-today',
            ].filter(Boolean).join(' ');
            const selected = isStart || (isEnd && !!end);
            const note = isStart ? ', check-in' : isEnd && end ? ', check-out' : '';
            return (
              <button
                type="button"
                key={key(day)}
                data-day={key(day)}
                className={classes}
                disabled={past}
                tabIndex={sameDay(day, focusDay) ? 0 : -1}
                aria-pressed={selected}
                aria-label={`${longLabel(day)}${note}`}
                onClick={() => choose(day)}
                onMouseEnter={() => setHover(day)}
                onFocus={() => setHover(day)}
              >
                <span>{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="cal" id={id} ref={gridRef} onKeyDown={onKeyDown} onMouseLeave={() => setHover(null)}>
      <div className="cal-nav">
        <button type="button" className="cal-nav-btn" onClick={() => setMonth(addMonths(month, -1))} disabled={!canGoBack} aria-label="Previous month">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
        </button>
        <p className="cal-hint">{selecting === 'start' || !start ? 'Choose your check-in' : 'Now choose your check-out'}</p>
        <button type="button" className="cal-nav-btn" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </button>
      </div>
      <div className={`cal-months${twoUp ? ' is-two' : ''}`}>
        {renderMonth(month, 0)}
        {twoUp && renderMonth(addMonths(month, 1), 1)}
      </div>
    </div>
  );
};

export default DateRangeCalendar;
